/**************************************************************************************************/
/*   File: /src/app/stores/effects/download/effects.ts                                            */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Wed Apr 17 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { map, mergeMap, switchMap, catchError, tap, flatMap, concatMap, withLatestFrom, filter } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';

import * as fromShared from '../../../shared';
import * as fromActions from '../../actions';
import * as fromStates from '../../states';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';

/**
 * The router effect
 */
@Injectable()
export class DownloadEffects {
  /**
   * The select plan effect
   */
  @Effect()
  selectPlanEffect$ = this.actions$.pipe(
    ofType(fromActions.fromDownload.DownloadActionsType.SELECT_PLAN_TYPE),
    map((action: fromActions.fromDownload.SelectPlanType) =>
      new fromActions.fromDownload.LoadAmbitiByPlan({
        planCode: action.payload.planCode
      })
    )
  );
  /**
   * The select plan effect
   */
  @Effect()
  rehydrateDossierContent$ = this.actions$.pipe(
    ofType(fromActions.fromDownload.DownloadActionsType.REHYDRATE_DOSSIER_CONTENT),
    map((action: fromActions.fromDownload.RehydrateDossierContent) =>
      new fromActions.fromDownload.LoadAmbitiByPlan({
        planCode: action.payload.dossierContent.planCode
      })
    )
  );

  /**
   * The request authorization
   */
  @Effect()
  prepareDownload$ = this.actions$.pipe(
    ofType(fromActions.fromDownload.DownloadActionsType.PREPARE_GEOMETRIC_DOWNLOAD),
    mergeMap((action: fromActions.fromDownload.PrepareGeometricDownload) =>

      this.downloadService.prepareDownload(action.payload.data).pipe(
        map((response) => {
          console.log('the effect is', response.taskId);
          return new fromActions.fromDownload.PrepareGeometricDownloadSuccess({
            taskId: response.taskId
          });
        }),
        catchError((fail) =>
          of(
            new fromActions.fromDownload.PrepareGeometricDownloadFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.WARNING,
                  fromShared.enums.Messages.CANNOT_CONTACT_SERVER,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromDownload.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );
  /**
   * The request authorization
   */
  @Effect()
  prepareRegulationsDownload$ = this.actions$.pipe(
    ofType(fromActions.fromDownload.DownloadActionsType.PREPARE_REGULATIONS_DOWNLOAD),
    flatMap((action: fromActions.fromDownload.PrepareRegulationsDownload) => {
      const arrayOfActions = [];
      action.payload.ambiti.forEach((ambito: fromShared.models.Ambito) => {
        arrayOfActions.push(
          new fromActions.fromDownload.PrepareRegulationForAmbitoDownload({
            ambitoType: ambito.ambitoTerritorialeType,
            ambitoCode: ambito.value,
            planCode: action.payload.plan
          })
        );
      });
      return arrayOfActions;
    })
  );

  /**
  * The request authorization
  */
  @Effect()
  prepareRegulationsDownloadForAmbito$ = this.actions$.pipe(
    ofType(fromActions.fromDownload.DownloadActionsType.PREPARE_REGULATION_FOR_AMBITO_DOWNLOAD),
    concatMap((action: fromActions.fromDownload.PrepareRegulationForAmbitoDownload) =>
      this.downloadService.prepareDownloadRegulation(action.payload.ambitoType, action.payload.ambitoCode, action.payload.planCode).pipe(
        map((response: any) =>
          new fromActions.fromDownload.PrepareRegulationForAmbitoDownloadSuccess({
            regulationResponse: new fromShared.models.RegulationResponse(response, action.payload.ambitoCode)
          })
        ),
        catchError((fail) =>
          of(
            new fromActions.fromDownload.PrepareRegulationForAmbitoDownloadFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.WARNING,
                  fromShared.enums.Messages.CANNOT_CONTACT_SERVER,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromDownload.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );
  /**
   * The request authorization
   */
  @Effect()
  checkDownloadStatusEffect$ = this.actions$.pipe(
    ofType(fromActions.fromDownload.DownloadActionsType.CHECK_DOWNLOAD_STATUS),
    mergeMap((action: fromActions.fromDownload.CheckDownloadStatus) =>

      this.downloadService.checkDownloadStatus(action.payload.taskId).pipe(
        map((response: any) =>
          new fromActions.fromDownload.CheckDownloadStatusSuccess({
            completed: response.complete
          })
        ),
        catchError((fail) =>
          of(
            new fromActions.fromDownload.CheckDownloadStatusFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.WARNING,
                  fromShared.enums.Messages.CANNOT_CONTACT_SERVER,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromDownload.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );


  /**
   * Load ambiti by plan
   */
  @Effect()
  loadAmbitiByPlanEffect$ = this.actions$.pipe(
    ofType<fromActions.fromDownload.LoadAmbitiByPlan>(
      fromActions.fromDownload.DownloadActionsType.LOAD_AMBITI_BY_PLAN
    ),
    switchMap((action) =>
      this.ambitiService.getApprovedAmbitiByPlanCode(action.payload.planCode).pipe(
        map(ambiti =>
          new fromActions.fromDownload.LoadAmbitiByPlanSuccess(
            {
              ambiti: ambiti
            }
          )
        ),
        catchError((fail) =>
          of(
            new fromActions.fromDownload.LoadAmbitiByPlanFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.WARNING,
                  fromShared.enums.Messages.AMBITI_FAIL,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromDownload.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );
  /**
   * The select plan effect
   */
  @Effect()
  loadPublicDocumentEffect$ = this.actions$.pipe(
    ofType(fromActions.fromDownload.DownloadActionsType.LOAD_PUBLIC_DOCUMENT),
    switchMap((action: fromActions.fromDownload.LoadPublicDocument) =>
      this.downloadService.getAllPublicDocuments(action.payload.plan, action.payload.type).pipe(
        map((publicDocuments: fromShared.models.PublicDocument[]) =>
          new fromActions.fromDownload.LoadPublicDocumentSuccess(
            {
              documents: publicDocuments
            }
          )
        ),
        catchError((fail) =>
          of(
            new fromActions.fromDownload.LoadPublicDocumentFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.WARNING,
                  fromShared.enums.Messages.CANNOT_LOAD_PUBLIC_DOCUMENT,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromDownload.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );

  /**
  * Reload summaries on language changed
  */
  @Effect()
  reloadDossiersEffect$ = this.actions$.pipe(
    ofType<fromActions.fromUi.LanguageChangeSuccessAction>(
      fromActions.fromUi.UiActionTypes.LANGUAGE_CHANGE_SUCCESS_ACTION
    ),
    // tslint:disable-next-line
    withLatestFrom(this.store, (action, state) => state.download.dossierContent),
    filter(dossierContent => dossierContent !== undefined),
    // withLatestFrom(this.store.select(fromSelectors.fromCreation.getDossierContent)),
    map(dossierContent =>
      new fromActions.fromDownload.LoadAmbitiByPlan({
        planCode: dossierContent.planCode
      })
    )
  );

  /**
   * The contructor
   * @param store The store
   * @param actions$ The actions
   * @param downloadService The downloadService
   * @param ambitiService The ambitiService
   */
  constructor(
    private store: Store<fromStates.fromRoot.RootState>,
    private actions$: Actions,
    private downloadService: fromShared.services.DownloadService,
    private ambitiService: fromShared.services.AmbitiService
  ) { }

}

